import { Copy, ExternalLink, Mail, MessageCircle, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClipboard } from '@/hooks/use-clipboard';
import { getDisplayPathFromUrl, isNativeShareSupported } from '@/lib/share';

interface SidebarShareCardProps {
  profileLink: string;
}

export function SidebarShareCard({ profileLink }: SidebarShareCardProps) {
  const { success, error } = useToast();
  const { copy, copied } = useClipboard();
  const nativeShareText =
    'Check out my caregiver profile. Feel free to book a time that works for you!';
  const shareMessageWithLink = `${nativeShareText} ${profileLink}`;
  const nativeShareSupported = isNativeShareSupported(profileLink);
  const displayPath = getDisplayPathFromUrl(profileLink);

  const handleCopyLink = async () => {
    const successResult = await copy(profileLink);
    if (successResult) {
      success('Link copied to clipboard!');
    } else {
      error('Failed to copy link. Please try again.');
    }
  };

  const handlePreviewProfile = () => {
    window.open(profileLink, '_blank', 'noopener,noreferrer');
  };

  const handleShareViaSMS = () => {
    const encodedShareMessage = encodeURIComponent(shareMessageWithLink);
    window.open(`sms:?body=${encodedShareMessage}`, '_self');
  };

  const handleShareViaEmail = () => {
    const subject = encodeURIComponent('My Caregiver Profile');
    const body = encodeURIComponent(
      `Hi!\n\nI wanted to share my caregiver profile with you:\n${profileLink}\n\nFeel free to book a time that works for you!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  const handleShareProfile = async () => {
    if (!nativeShareSupported) {
      return;
    }

    try {
      await navigator.share({
        title: 'My Caregiver Profile',
        text: nativeShareText,
        url: profileLink,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      error('Failed to open share options. Please try again.');
    }
  };

  return (
    <div className="bg-lilac-500 md:bg-white rounded-xl p-2.5 border border-lilac-900 shadow-sm overflow-hidden max-w-full">
      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-body text-sm font-medium text-night-700">
            Your Profile
          </span>
          <button
            onClick={handlePreviewProfile}
            className="ml-auto inline-flex items-center gap-1 shadow-sm rounded-full border border-blueviolet bg-white px-3 py-1 text-xs font-semibold text-blueviolet hover:bg-lilac-500 hover:text-blueviolet-dark hover:border-blueviolet-dark transition-all"
            aria-label="Open profile"
            title="Open profile"
          >
            <span>View</span>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex items-center bg-white rounded-md px-2 py-1 mb-2 min-w-0">
          <a
            href={profileLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-0 py-1 font-mono text-xs text-blueviolet hover:text-blueviolet-light truncate transition-colors"
            title={profileLink}
          >
            {displayPath}
          </a>
        </div>

        <div className="flex justify-between gap-2">
          <button
            onClick={handleCopyLink}
            disabled={copied}
            className={`w-full flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 shadow-sm transition-all ${
              copied
                ? 'text-success-dark bg-success border-success-dark'
                : 'text-blueviolet bg-white border-blueviolet'
            }`}
            aria-label={copied ? 'Link copied' : 'Copy profile link'}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            <span className="font-body text-sm font-semibold">{copied ? 'Copied' : 'Copy'}</span>
            <Copy className="h-4 w-4" aria-hidden="true" />
          </button>

          {nativeShareSupported && (
            <button
              onClick={handleShareProfile}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-blueviolet px-3 py-2 text-blueviolet bg-white shadow-sm hover:text-blueviolet-dark hover:bg-lilac-500 hover:border-blueviolet-dark transition-all"
              aria-label="Share profile"
              title="Share profile"
            >
              <span className="font-body text-sm font-semibold">Share</span>
              <Share2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-body text-sm font-medium text-night-700 truncate">
            Your Profile
          </span>
          <button
            onClick={handlePreviewProfile}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-blueviolet bg-white px-3 py-1 text-xs font-semibold text-blueviolet hover:bg-lilac-500 hover:text-blueviolet-dark hover:border-blueviolet-dark hover:shadow-sm transition-all"
            aria-label="Open profile"
            title="Open profile"
          >
            <span>VIEW</span>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex items-center gap-1 bg-lilac-500 rounded-lg px-2 py-1.5 mb-2 min-w-0">
          <a
            href={profileLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-0 py-1 font-mono text-xs text-blueviolet hover:text-blueviolet-light truncate transition-colors"
            title={profileLink}
          >
            {displayPath}
          </a>
          <button
            onClick={handleCopyLink}
            disabled={copied}
            className={`p-1.5 rounded-full transition-all flex-shrink-0 hover:shadow-md ${
              copied
                ? 'text-success-dark bg-success'
                : 'text-blueviolet hover:text-white hover:bg-blueviolet'
            }`}
            aria-label={copied ? 'Link copied' : 'Copy profile link'}
            title={copied ? 'Copied!' : 'Copy link'}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-body text-xs uppercase text-night-500 font-medium flex-shrink-0">
            Share
          </span>
          <div className="flex-1 h-px bg-lilac-900 min-w-0" />
          <button
            onClick={handleShareViaSMS}
            className="p-1.5 rounded-full text-blueviolet hover:text-white hover:bg-blueviolet transition-all hover:shadow-md flex-shrink-0"
            aria-label="Share via text message"
            title="Text"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            onClick={handleShareViaEmail}
            className="p-1.5 rounded-full text-blueviolet hover:text-white hover:bg-blueviolet transition-all hover:shadow-md flex-shrink-0"
            aria-label="Share via email"
            title="Email"
          >
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
